import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  buildMatchSnippet,
  matchesTokens,
  parseSearchTokens,
} from "../../services/backend/FiledSearchService";

// Shared list engine for /filedlist and /filedlist/requests: cursor pagination
// plus a background auto-cascade so a search reaches past the first page.
// Mirrors PqrsdAdminList, which does the same over the same collection.
const PAGE_SIZE = 25;
// Each scroll-triggered burst pulls at most this many rows before pausing, so a
// broad search never quietly reads the whole collection. Radicados carry OCR
// text, so a runaway cascade costs bandwidth as well as reads.
const CASCADE_BATCH = 50;
// Below this the cascade stays off — a single stray character shouldn't start
// pulling pages. Filtering of already-loaded rows still happens.
const MIN_CASCADE_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 300;

export const FILED_RANGES = {
  TODAY: "TODAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
  LAST_MONTH: "LAST_MONTH",
  CUSTOM: "CUSTOM",
  ALL: "ALL",
};

// Match the range buttons verbatim, so the status line names the control the
// user actually clicked.
export const FILED_RANGE_LABELS = {
  [FILED_RANGES.TODAY]: "Hoy",
  [FILED_RANGES.WEEK]: "Semana",
  [FILED_RANGES.MONTH]: "Mes",
  [FILED_RANGES.LAST_MONTH]: "Mes pasado",
  [FILED_RANGES.CUSTOM]: "Personalizado",
  [FILED_RANGES.ALL]: "Todos",
};

// Accepts a moment (from the DatePicker) or a Date.
const toDate = (value) => {
  if (!value) return null;
  const date = value.toDate ? value.toDate() : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

// Resolves a range key into createdAt bounds. ALL leaves both null so the query
// spans the whole history.
const resolveRange = (key, start, end) => {
  const now = new Date();
  let from = null;
  let to = null;
  switch (key) {
    case FILED_RANGES.ALL:
      break;
    case FILED_RANGES.WEEK: {
      // Build both bounds off a fixed anchor — deriving the end from an
      // already-mutated start is what made the old version drift.
      const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      from = weekStart;
      to = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
      break;
    }
    case FILED_RANGES.MONTH:
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      break;
    case FILED_RANGES.LAST_MONTH:
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      to = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case FILED_RANGES.CUSTOM:
      from = toDate(start) || new Date();
      to = toDate(end) || new Date();
      break;
    case FILED_RANGES.TODAY:
    default:
      from = new Date();
      to = new Date();
      break;
  }
  if (from) from.setHours(0, 0, 0, 0);
  if (to) to.setHours(23, 59, 59, 999);
  return { key, dateFrom: from, dateTo: to };
};

const dedupeAppend = (prev, incoming) => {
  if (!incoming.length) return prev;
  const seen = new Set(prev.map((f) => f.filedId));
  const next = prev.slice();
  for (const item of incoming) {
    if (seen.has(item.filedId)) continue;
    next.push(item);
    seen.add(item.filedId);
  }
  return next;
};

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Maps a Firestore error code onto something a user can act on. Without this a
// building index reads exactly like "no hay resultados".
export const filedLoadErrorMessage = (code) => {
  if (code === "permission-denied") return "No tienes permiso para consultar estos radicados.";
  if (code === "failed-precondition") {
    return "El índice se está construyendo. Vuelve a intentarlo en unos minutos.";
  }
  return "No se pudieron cargar los radicados. Intenta recargar.";
};

// `fetchPage` must be referentially stable (useCallback in the caller) — it is a
// dependency of loadPage, which drives the reset effect.
//
// `filterTypeOnServer`: true on /filedlist, where filedType is a top-level field
// with an index behind it. False on /filedlist/requests, where the type lives
// under `request` — there the radio filters client-side only, so switching it
// must not reset pagination and re-read the same rows.
export default function useFiledSearch({
  fetchPage,
  filedType = "all",
  enabled = true,
  filterTypeOnServer = true,
}) {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState(() => resolveRange(FILED_RANGES.TODAY));
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  // Soft cap for the background cascade. Starts at one batch and grows a batch
  // at a time as the user scrolls, so broad searches fetch in throttled bursts.
  const [cascadeCap, setCascadeCap] = useState(CASCADE_BATCH);

  // Firestore DocumentSnapshot cursors are non-serializable and only valid
  // while mounted, so position lives in a ref rather than state.
  const cursorRef = useRef(null);
  const inFlightRef = useRef(false);
  // Bumped on every reset so an in-flight page from a previous filter
  // generation can detect it is stale and bail out before touching state.
  const generationRef = useRef(0);
  const sentinelRef = useRef(null);

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const tokens = useMemo(() => parseSearchTokens(debouncedSearch), [debouncedSearch]);
  const searchLength = useMemo(() => tokens.join("").length, [tokens]);
  const cascadeMode = searchLength >= MIN_CASCADE_CHARS;

  const applyRange = useCallback((key, start, end) => {
    setRange(resolveRange(key, start, end));
  }, []);

  const serverFiledType =
    filterTypeOnServer && filedType && filedType !== "all" ? filedType : null;

  const loadPage = useCallback(
    async (reset) => {
      if (!enabled) return;
      if (!reset && inFlightRef.current) return;
      if (reset) {
        generationRef.current += 1;
        cursorRef.current = null;
        setHasMore(true);
        setCascadeCap(CASCADE_BATCH);
        setLoadError(null);
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const myGen = generationRef.current;
      inFlightRef.current = true;
      try {
        const { items, lastDoc, hasMore: more, error } = await fetchPage({
          filedType: serverFiledType,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          cursor: cursorRef.current,
          pageSize: PAGE_SIZE,
        });
        if (myGen !== generationRef.current) return;
        cursorRef.current = lastDoc;
        setHasMore(more);
        setLoadError(error || null);
        if (reset) setList(items);
        else setList((prev) => dedupeAppend(prev, items));
      } finally {
        if (myGen === generationRef.current) {
          inFlightRef.current = false;
          setLoadingMore(false);
          if (reset) setLoading(false);
        }
      }
    },
    [enabled, fetchPage, serverFiledType, range.dateFrom, range.dateTo]
  );

  // First load, and a full reset whenever the query itself changes. Search text
  // is deliberately absent: the loaded set is a superset either way, so typing
  // never discards rows or restarts pagination.
  useEffect(() => {
    if (!enabled) return;
    loadPage(true);
  }, [enabled, loadPage]);

  // Infinite scroll. While cascading, the cascade already does the fetching, so
  // reaching the end instead raises the soft cap for one more burst.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || !hasMore || inFlightRef.current) return;
        if (cascadeMode) {
          if (list.length >= cascadeCap) setCascadeCap(list.length + CASCADE_BATCH);
        } else {
          loadPage(false);
        }
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage, list.length, cascadeMode, cascadeCap]);

  // Auto-cascade while searching: keep pulling pages until we run out or hit
  // the current cap. Matches surface as each chunk lands.
  const cascading = cascadeMode && hasMore && list.length < cascadeCap && !loadError;
  useEffect(() => {
    if (!cascading || inFlightRef.current) return;
    loadPage(false);
  }, [cascading, list.length, loadingMore, loadPage]);

  // Re-arm the cap on the DEBOUNCED value. Re-arming per keystroke would keep
  // dropping the cap below list.length and stall the cascade mid-word.
  useEffect(() => {
    setCascadeCap(CASCADE_BATCH);
  }, [debouncedSearch]);

  const filtered = useMemo(() => {
    let rows = list;
    // A no-op on /filedlist, where fetchFiledPage already applied it server-side;
    // the real type filter on /filedlist/requests, where filedType sits under
    // `request` and would need another index leg to query.
    if (filedType && filedType !== "all") rows = rows.filter((f) => f.filedType === filedType);
    if (tokens.length) rows = rows.filter((row) => matchesTokens(row, tokens));
    return rows;
  }, [list, filedType, tokens]);

  // Snippets are built only for rows that survived the filter — never over the
  // full loaded list.
  const items = useMemo(() => {
    if (!tokens.length) return filtered;
    return filtered.map((row) => ({ ...row, match: buildMatchSnippet(row, tokens) }));
  }, [filtered, tokens]);

  const statusMessage = useMemo(() => {
    // Whenever a search is bounded by a period, say so — otherwise someone
    // hunting an old radicado inside "Hoy" concludes the search is broken.
    const rangeHint =
      range.key !== FILED_RANGES.ALL
        ? ` · Solo en el periodo «${FILED_RANGE_LABELS[range.key]}» — elige «Todos» para buscar en todo el histórico`
        : "";
    if (tokens.length) {
      if (!cascadeMode) {
        return `Escribe al menos ${MIN_CASCADE_CHARS} caracteres para buscar dentro de los documentos.`;
      }
      if (cascading) {
        return `Buscando dentro de los documentos… ${filtered.length} de ${list.length} revisados${rangeHint}`;
      }
      if (!hasMore) {
        return `${filtered.length} coincidencias en ${list.length} radicados${rangeHint}`;
      }
      return `${filtered.length} coincidencias en los primeros ${list.length} · desplázate para seguir buscando${rangeHint}`;
    }
    if (!hasMore) {
      return `${list.length} radicados · ${FILED_RANGE_LABELS[range.key]}`;
    }
    return `${list.length} radicados cargados · desplázate para ver más`;
  }, [tokens.length, cascadeMode, cascading, hasMore, list.length, filtered.length, range.key]);

  const refresh = useCallback(() => loadPage(true), [loadPage]);

  return {
    items,
    loadedCount: list.length,
    matchCount: filtered.length,
    search,
    setSearch,
    isSearching: tokens.length > 0,
    range,
    applyRange,
    loading,
    loadingMore,
    cascading,
    hasMore,
    loadError,
    statusMessage,
    sentinelRef,
    refresh,
  };
}
